// Public, no-admin-session client portal. A client authenticates with their
// BasicUser credentials (username/password) and sees a lean list of their own
// services — name, domain/URL, status and live CPU/mem/disk usage. Rendered
// outside the admin-login gate (see App.tsx) so it needs no operator session.

import { useState } from 'react';
import { I } from '../components/icons';
import { Logo, StatusBadge, ServiceIcon } from '../components/ui';
import { lookupServicesByBasicAuth, ApiError, type PublicLookupResult } from '../api/client';

export function ServicesLookupScreen() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<PublicLookupResult | null>(null);
  const [signedInAs, setSignedInAs] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!username.trim() || !password) { setErr('Enter your username and password.'); return; }
    setLoading(true);
    try {
      const res = await lookupServicesByBasicAuth(username.trim(), password);
      setResult(res);
      setSignedInAs(username.trim());
      setPassword('');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setSignedInAs('');
    setUsername('');
    setPassword('');
    setErr(null);
  };

  // ── Results view ───────────────────────────────────────────────────────────
  if (result !== null) {
    return (
      <div className="login-wrap">
        <div className="login-card card card-pad-lg" style={{ maxWidth: 720 }}>
          <div className="login-brand">
            <Logo size={28}/>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, letterSpacing: '-.01em' }}>Shoptefy</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.12em', marginTop: -2 }}>Client Portal</div>
            </div>
          </div>
          <div className="spread" style={{ alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ fontSize: 20 }}>{result.displayName || result.clientId}</h1>
              <p className="sub" style={{ margin: '4px 0 0' }}>
                Client <span className="mono" style={{ color: 'var(--text-1)' }}>{result.clientId}</span>
                <span className="muted"> · signed in as <span className="mono">{signedInAs}</span></span>
              </p>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={reset}>
              <I.LogOut size={13}/> Sign out
            </button>
          </div>

          <div className="divider"></div>

          {result.services.length === 0 ? (
            <div className="empty">
              <div className="empty-icon"><I.Layers size={28}/></div>
              No services are provisioned for your account yet.
            </div>
          ) : (
            <div className="col" style={{ gap: 12 }}>
              {result.services.map((svc) => (
                <div key={svc.name} className="card card-pad" style={{ background: 'var(--bg-1)' }}>
                  <div className="spread" style={{ marginBottom: 10 }}>
                    <div className="row" style={{ gap: 9 }}>
                      <ServiceIcon name={svc.name} size={15}/>
                      <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{svc.name}</span>
                    </div>
                    <StatusBadge status={svc.status}/>
                  </div>

                  <div className="col" style={{ fontSize: 12.5, gap: 8 }}>
                    {/* In-cluster endpoints — one row per k8s Service, labelled
                        with the object name, each carrying its own fqdn:port(s). */}
                    <div>
                      <div className="muted" style={{ marginBottom: 5 }}>Internal endpoints</div>
                      <div className="col" style={{ gap: 5 }}>
                        {/* One <fqdn>:<port> line per (Service, port) so a Service
                            with several ports (e.g. nginx 80+443) isn't crammed. */}
                        {svc.endpoints.flatMap((ep) =>
                          (ep.ports.length ? ep.ports : [null]).map((p) => (
                            <div key={ep.name + ':' + p} className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'nowrap' }}>
                              <span className="badge" style={{ fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{ep.name}</span>
                              <span className="mono" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {ep.fqdn}{p !== null && <span style={{ color: 'var(--accent)' }}>:{p}</span>}
                              </span>
                            </div>
                          )),
                        )}
                      </div>
                    </div>
                    {/* External ingress URL — only when a domain is configured. */}
                    <div className="row" style={{ gap: 6 }}>
                      <span className="muted">External</span>
                      {svc.url ? (
                        <a href={svc.url} target="_blank" rel="noopener"
                           className="mono row" style={{ gap: 6, color: 'var(--accent)', width: 'fit-content' }}>
                          <I.ExtLink size={12}/> {svc.url.replace(/^https?:\/\//, '')}
                        </a>
                      ) : (
                        <span className="muted">no domain configured</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Login form view ────────────────────────────────────────────────────────
  return (
    <div className="login-wrap">
      <div className="login-card card card-pad-lg">
        <div className="login-brand">
          <Logo size={28}/>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, letterSpacing: '-.01em' }}>Shoptefy</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.12em', marginTop: -2 }}>Client Portal</div>
          </div>
        </div>
        <h1>View your services</h1>
        <p className="sub">Sign in with the credentials provided by your operator.</p>

        <form onSubmit={submit} className="col" style={{ gap: 14 }}>
          <div className="field">
            <label className="field-label">Username</label>
            <div style={{ position: 'relative' }}>
              <I.Users size={14} style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-3)' }}/>
              <input className="input" style={{ paddingLeft: 34 }} type="text"
                     value={username} onChange={(e) => setUsername(e.target.value)}
                     autoComplete="username" autoFocus/>
            </div>
          </div>
          <div className="field">
            <label className="field-label">Password</label>
            <div style={{ position: 'relative' }}>
              <I.Lock size={14} style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-3)' }}/>
              <input className="input" style={{ paddingLeft: 34, paddingRight: 36 }}
                     type={showPw ? 'text' : 'password'} value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     autoComplete="current-password"
                     placeholder="••••••••••"/>
              <button type="button" className="btn btn-ghost btn-icon btn-sm"
                      style={{ position: 'absolute', right: 3, top: 3 }}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPw(!showPw)}>
                {showPw ? <I.EyeOff size={14}/> : <I.Eye size={14}/>}
              </button>
            </div>
          </div>
          {err && <div className="field-error">{err}</div>}
          <button className="btn btn-primary btn-lg" type="submit" disabled={loading}
                  style={{ marginTop: 6 }}>
            {loading ? <><span className="spinner"/> Looking up…</> : <>View services <I.ArrowR size={14}/></>}
          </button>
        </form>
      </div>
    </div>
  );
}
